/**
 * @dc/dads-components — Vendored stub.
 *
 * Provides functional React component stubs for CI builds, air-gapped
 * environments, and Containerfile builds where the real @dc/dads-components
 * package is unavailable.
 *
 * To use real components:
 *   1. Update package.json to point to CodeArtifact or local sibling repo
 *   2. Run `npm install`
 *
 * These stubs render semantic HTML elements with Tailwind utility classes
 * so the app is usable during development without the real design system.
 */
import { createElement, forwardRef } from 'react';

// ── Alert ──────────────────────────────────────────────────────────────

const alertVariants = {
  default: 'bg-blue-50 text-blue-800 border-blue-200',
  destructive: 'bg-red-50 text-red-800 border-red-200',
  danger: 'bg-red-50 text-red-800 border-red-200',
  success: 'bg-green-50 text-green-800 border-green-200',
  warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

export function Alert({ variant = 'default', className = '', children, ...rest }) {
  const v = alertVariants[variant] || alertVariants.default;
  return createElement('div', { role: 'alert', className: `rounded-md border p-4 text-sm ${v} ${className}`, ...rest }, children);
}

export function AlertDescription({ className = '', children, ...rest }) {
  return createElement('div', { className: `text-sm ${className}`, ...rest }, children);
}

export function AlertTitle({ className = '', children, ...rest }) {
  return createElement('h5', { className: `font-medium leading-none tracking-tight ${className}`, ...rest }, children);
}

// ── Badge ──────────────────────────────────────────────────────────────

const badgeVariants = {
  default: 'bg-blue-100 text-blue-800',
  secondary: 'bg-gray-100 text-gray-800',
  destructive: 'bg-red-100 text-red-800',
  outline: 'border border-gray-300 text-gray-700 bg-transparent',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
};

export function Badge({ variant = 'default', pill, className = '', children, ...rest }) {
  const v = badgeVariants[variant] || badgeVariants.default;
  return createElement('span', { className: `inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${v} ${className}`, ...rest }, children);
}

// ── Button ─────────────────────────────────────────────────────────────

const buttonVariants = {
  default: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
  destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
  outline: 'border border-border bg-surface text-content hover:opacity-80',
  secondary: 'bg-surface border border-border text-content hover:opacity-80',
  ghost: 'text-gray-700 hover:bg-gray-100',
  link: 'text-blue-600 underline-offset-4 hover:underline',
};

const buttonSizes = {
  default: 'h-10 px-4 py-2',
  sm: 'h-9 px-3 text-sm',
  lg: 'h-11 px-6 text-base',
  icon: 'h-10 w-10',
};

export const Button = forwardRef(function Button({ variant = 'default', size = 'default', className = '', children, ...rest }, ref) {
  const v = buttonVariants[variant] || buttonVariants.default;
  const s = buttonSizes[size] || buttonSizes.default;
  return createElement('button', {
    ref,
    'data-variant': variant,
    'data-size': size,
    className: `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${v} ${s} ${className}`,
    ...rest,
  }, children);
});

export function ButtonGroup({ className = '', children, ...rest }) {
  return createElement('div', { className: `inline-flex rounded-md shadow-sm ${className}`, ...rest }, children);
}

// ── Checkbox ───────────────────────────────────────────────────────────

export function Checkbox({ checked, onCheckedChange, disabled, className = '', ...rest }) {
  return createElement('input', {
    type: 'checkbox',
    checked,
    disabled,
    onChange: (e) => onCheckedChange?.(e.target.checked),
    className: `h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`,
    ...rest,
  });
}

// ── Input ──────────────────────────────────────────────────────────────

export const Input = forwardRef(function Input({ className = '', ...rest }, ref) {
  return createElement('input', {
    ref,
    className: `flex w-full rounded-md border border-border bg-surface text-content px-3 py-2 text-sm shadow-sm placeholder:text-content/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent disabled:cursor-not-allowed disabled:opacity-50 ${className}`,
    ...rest,
  });
});

// ── Label ──────────────────────────────────────────────────────────────

export const Label = forwardRef(function Label({ className = '', children, ...rest }, ref) {
  return createElement('label', {
    ref,
    className: `text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`,
    ...rest,
  }, children);
});

// ── NativeSelect ───────────────────────────────────────────────────────

export const NativeSelect = forwardRef(function NativeSelect({ className = '', children, ...rest }, ref) {
  // Design-system contract for NativeSelect (post-PR-#42 alignment):
  //
  // Browser-native `<select>` elements have their own intrinsic sizing
  // rules — they auto-size to their displayed option's text width and
  // their height comes from `font-size + line-height + vertical
  // padding`. Unlike a `<div>`, you cannot fully detach a select's
  // height from its font size + padding.
  //
  // The stub ships SENSIBLE DEFAULTS so the select looks good in the
  // common form-row case without any caller styling, AND remains
  // overrideable by tailwind later-wins for unusual contexts.
  //
  // The ONLY width hint is `w-fit`: sizes the select to its content
  // (not the parent's full row). Form-row consumers pass `w-full`;
  // toolbar consumers do nothing.
  //
  // No min-w. No max-w. No height. Padding (`px-3 py-2`) and font
  // (`text-sm`) make the natural rendered height ~36px which is the
  // platform's design-spec touch target for interactive elements.
  // Consumers that want a tighter look should NOT pass `h-7` (it
  // fights the padding and clips the text); they should pass a
  // matched `py-1 text-xs` pair instead.
  return createElement('select', {
    ref,
    className: `w-fit rounded-md border border-border bg-surface text-content px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent disabled:cursor-not-allowed disabled:opacity-50 ${className}`,
    ...rest,
  }, children);
});

// ── RadioGroup ─────────────────────────────────────────────────────────

export function RadioGroup({ value, onValueChange, className = '', children, ...rest }) {
  return createElement('div', { role: 'radiogroup', className: `grid gap-2 ${className}`, ...rest }, children);
}

export function RadioGroupItem({ value, className = '', ...rest }) {
  return createElement('input', {
    type: 'radio',
    value,
    className: `h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`,
    ...rest,
  });
}

// ── Select ─────────────────────────────────────────────────────────────

export function Select({ value, onValueChange, className = '', children, ...rest }) {
  return createElement('div', { className: `relative ${className}`, ...rest }, children);
}

export function SelectContent({ className = '', children, ...rest }) {
  return createElement('div', { className, ...rest }, children);
}

export function SelectItem({ value, className = '', children, ...rest }) {
  return createElement('option', { value, className, ...rest }, children);
}

export function SelectTrigger({ className = '', children, ...rest }) {
  return createElement('div', { className, ...rest }, children);
}

export function SelectValue({ placeholder, ...rest }) {
  return createElement('span', rest, placeholder);
}

// ── Separator ──────────────────────────────────────────────────────────

export function Separator({ orientation = 'horizontal', className = '', ...rest }) {
  const cls = orientation === 'vertical'
    ? 'w-px h-full bg-gray-200'
    : 'h-px w-full bg-gray-200';
  return createElement('div', { role: 'separator', className: `shrink-0 ${cls} ${className}`, ...rest });
}

// ── Stub components (air-gapped fallback) ─────────────────────────────
// These match the real package's export surface so air-gapped builds
// compile without the CodeArtifact registry. Minimal implementations.

const noop = (props) => createElement('div', props, props.children);
const noopSpan = (props) => createElement('span', props, props.children);

// Tabs
export function Tabs({ className = '', children, ...rest }) { return createElement('div', { className, ...rest }, children); }
export function TabsList({ className = '', children, ...rest }) { return createElement('div', { role: 'tablist', className: `inline-flex items-center gap-1 ${className}`, ...rest }, children); }
export function TabsTrigger({ className = '', children, value, ...rest }) { return createElement('button', { role: 'tab', className: `px-3 py-1.5 text-sm font-medium rounded-md ${className}`, ...rest }, children); }
export function TabsContent({ className = '', children, value, ...rest }) { return createElement('div', { role: 'tabpanel', className, ...rest }, children); }

// Accordion
export function Accordion({ className = '', children, ...rest }) { return createElement('div', { className, ...rest }, children); }
export function AccordionItem({ className = '', children, ...rest }) { return createElement('div', { className: `border-b ${className}`, ...rest }, children); }
export function AccordionTrigger({ className = '', children, ...rest }) { return createElement('button', { className: `flex w-full items-center justify-between py-4 font-medium ${className}`, ...rest }, children); }
export function AccordionContent({ className = '', children, ...rest }) { return createElement('div', { className: `pb-4 ${className}`, ...rest }, children); }

// Popover
export const Popover = noop;
export const PopoverTrigger = noop;
export const PopoverContent = noop;

// DropdownMenu
export const DropdownMenu = noop;
export const DropdownMenuTrigger = noop;
export function DropdownMenuContent({ className = '', children, ...rest }) { return createElement('div', { className: `rounded-md border bg-white p-1 shadow-md ${className}`, ...rest }, children); }
export function DropdownMenuItem({ className = '', children, ...rest }) { return createElement('div', { role: 'menuitem', className: `px-2 py-1.5 text-sm cursor-pointer rounded-sm hover:bg-gray-100 ${className}`, ...rest }, children); }
export const DropdownMenuSeparator = () => createElement('div', { className: 'h-px my-1 bg-gray-200' });
export const DropdownMenuLabel = noopSpan;
export const DropdownMenuGroup = noop;
export const DropdownMenuCheckboxItem = noop;
export const DropdownMenuRadioGroup = noop;
export const DropdownMenuRadioItem = noop;
export const DropdownMenuSub = noop;
export const DropdownMenuSubTrigger = noop;
export const DropdownMenuSubContent = noop;
export const DropdownMenuPortal = noop;
export const DropdownMenuShortcut = noopSpan;

// Tooltip
export const Tooltip = noop;
export const TooltipTrigger = noop;
export const TooltipContent = noop;
export const TooltipProvider = noop;

// Switch
export function Switch({ checked, onCheckedChange, className = '', ...rest }) {
  return createElement('button', { role: 'switch', 'aria-checked': checked, className: `relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200'} ${className}`, onClick: () => onCheckedChange?.(!checked), ...rest },
    createElement('span', { className: `inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}` })
  );
}

// Progress
export function Progress({ value = 0, className = '', ...rest }) {
  return createElement('div', { role: 'progressbar', 'aria-valuenow': value, className: `relative h-2 w-full overflow-hidden rounded-full bg-gray-200 ${className}`, ...rest },
    createElement('div', { className: 'h-full bg-blue-600 transition-all', style: { width: `${value}%` } })
  );
}

// Avatar
export function Avatar({ className = '', children, ...rest }) { return createElement('span', { className: `relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full ${className}`, ...rest }, children); }
export function AvatarImage({ src, alt, className = '', ...rest }) { return createElement('img', { src, alt, className: `aspect-square h-full w-full ${className}`, ...rest }); }
export function AvatarFallback({ className = '', children, ...rest }) { return createElement('span', { className: `flex h-full w-full items-center justify-center rounded-full bg-gray-100 ${className}`, ...rest }, children); }

// Skeleton
export function Skeleton({ className = '', ...rest }) { return createElement('div', { className: `animate-pulse rounded-md bg-gray-200 ${className}`, ...rest }); }

// Table
export function Table({ className = '', children, ...rest }) { return createElement('table', { className: `w-full caption-bottom text-sm ${className}`, ...rest }, children); }
export function TableHeader({ className = '', children, ...rest }) { return createElement('thead', { className, ...rest }, children); }
export function TableBody({ className = '', children, ...rest }) { return createElement('tbody', { className, ...rest }, children); }
export function TableFooter({ className = '', children, ...rest }) { return createElement('tfoot', { className, ...rest }, children); }
export function TableRow({ className = '', children, ...rest }) { return createElement('tr', { className, ...rest }, children); }
export function TableHead({ className = '', children, ...rest }) { return createElement('th', { className, ...rest }, children); }
export function TableCell({ className = '', children, ...rest }) { return createElement('td', { className, ...rest }, children); }
export function TableCaption({ className = '', children, ...rest }) { return createElement('caption', { className, ...rest }, children); }

// Card
export function Card({ className = '', children, ...rest }) { return createElement('div', { className: `rounded-xl border bg-white shadow ${className}`, ...rest }, children); }
export function CardHeader({ className = '', children, ...rest }) { return createElement('div', { className: `flex flex-col space-y-1.5 p-6 ${className}`, ...rest }, children); }
export function CardTitle({ className = '', children, ...rest }) { return createElement('div', { className: `font-semibold leading-none tracking-tight ${className}`, ...rest }, children); }
export function CardDescription({ className = '', children, ...rest }) { return createElement('div', { className: `text-sm text-gray-500 ${className}`, ...rest }, children); }
export function CardContent({ className = '', children, ...rest }) { return createElement('div', { className: `p-6 pt-0 ${className}`, ...rest }, children); }
export function CardFooter({ className = '', children, ...rest }) { return createElement('div', { className: `flex items-center p-6 pt-0 ${className}`, ...rest }, children); }
export function CardImage(props) { return createElement('img', props); }
export function CardAction({ className = '', children, ...rest }) { return createElement('div', { className, ...rest }, children); }

// Dialog
export const Dialog = noop;
export const DialogTrigger = noop;
export function DialogContent({ className = '', children, ...rest }) { return createElement('div', { role: 'dialog', className: `fixed inset-0 z-50 flex items-center justify-center ${className}`, ...rest }, createElement('div', { className: 'rounded-lg border bg-white p-6 shadow-lg max-w-lg w-full' }, children)); }
export function DialogHeader({ className = '', children, ...rest }) { return createElement('div', { className: `flex flex-col space-y-1.5 ${className}`, ...rest }, children); }
export function DialogTitle({ className = '', children, ...rest }) { return createElement('h2', { className: `text-lg font-semibold ${className}`, ...rest }, children); }
export function DialogDescription({ className = '', children, ...rest }) { return createElement('p', { className: `text-sm text-gray-500 ${className}`, ...rest }, children); }
export function DialogFooter({ className = '', children, ...rest }) { return createElement('div', { className: `flex justify-end gap-2 pt-4 ${className}`, ...rest }, children); }
export const DialogClose = noop;
export const DialogOverlay = noop;
export const DialogPortal = noop;

// Calendar, Slider (minimal stubs)
export const Calendar = noop;
export const Slider = noop;

// Toaster (sonner re-export in real package)
export const Toaster = noop;
